/**
 * QoCPublicSurvey.js
 * Public patient satisfaction survey — no login required.
 * Route: /qoc/survey/:type  (type = 'inpatient' | 'outpatient' | 'select')
 *
 * When type = 'select' OR no type, shows a landing page for patient to choose.
 * When type is inpatient/outpatient, loads the relevant form directly.
 */
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { Select, Input, Button, Progress, Tag, message, Spin } from 'antd';
import { CheckCircleOutlined, LoadingOutlined } from '@ant-design/icons';
import axios from 'axios';
import SS from '../../i18n/ss';
import moment from 'moment';

// ── i18n helper ───────────────────────────────────────────────────────────────
// EN strings are the original hardcoded values; SS provides Siswati equivalents.
// t(key) returns SS[key] when lang==='ss', otherwise returns key itself
// (keys are the English strings for most labels, or named keys for longer text).
const EN_NAMED = {
    invalid_access_title:   'Invalid Access',
    invalid_access_body:    'This survey can only be accessed by scanning the official QR code displayed at the health facility. Shared links are not accepted to protect the integrity of feedback.',
    moh_footer:             'Ministry of Health · Eswatini Quality of Care Program',
    moh_label:              'Ministry of Health · Eswatini',
    survey_title:           'Patient Satisfaction Survey',
    nqmp_subtitle:          'National Quality Management Programme\nYour feedback helps us improve healthcare services.',
    select_visit_type:      'Please select the type of visit you had:',
    inpatient_label:        'Inpatient',
    inpatient_desc:         'You were admitted and stayed overnight in the hospital',
    outpatient_label:       'Outpatient',
    outpatient_desc:        'You visited for a consultation and went home the same day',
    anonymous_note:         'All responses are anonymous and used only for service improvement.',
    too_far_title:          'You are not at this facility',
    too_far_body_pre:       'This survey is only for patients currently at ',
    too_far_body_post:      '.',
    too_far_distance:       'Your distance:',
    too_far_must_be:        'Must be within 100m of the facility',
    too_far_tip:            'If you are at the facility, please ensure location services are enabled and try scanning the QR code again.',
    location_required_title:'Location Access Required',
    location_required_body: 'This survey requires location access to confirm you are at the health facility.\n\nTo enable: tap the lock icon in your browser address bar → Site settings → Location → Allow. Then scan the QR code again.',
    location_not_stored:    'Your location is only used to verify you are at the facility. It is not stored or shared.',
    already_submitted_title:'Already Submitted',
    already_submitted_body: 'A survey for this facility has already been submitted from this device this year. Each device may only submit one response per facility per year to ensure fair and accurate feedback.',
    go_back:                '← Go Back',
    thank_you_title:        'Thank You!',
    thank_you_body:         'Your feedback has been submitted successfully.\nIt helps us improve healthcare services in Eswatini.',
    fill_another:           '← Fill Another Survey',
    draft_restored_title:   '📋 Draft restored',
    draft_restored_body:    'Your previous progress has been loaded. Continue from where you left off.',
    start_fresh:            'Start fresh',
    completion_label:       'Completion',
    select_facility:        '📍 Select Your Facility *',
    search_facility:        'Search for your facility...',
    patient_details:        '👤 Patient Details',
    gender_label:           'Gender',
    gender_male:            'Male',
    gender_female:          'Female',
    gender_other:           'Other',
    age_label:              'Age (years)',
    age_placeholder:        'e.g. 34',
    paid_question:          'Did you pay for any services today?',
    paid_yes:               'Yes',
    paid_no:                'No',
    amount_paid_label:      'How much did you pay? (Emalangeni)',
    amount_paid_placeholder:'e.g. 50',
    visit_details:          '📋 Visit Details',
    ward_admitted:          'Ward Admitted In',
    ward_placeholder:       'e.g. Medical Ward',
    depts_visited:          'Departments Visited',
    depts_placeholder:      'e.g. OPD, Pharmacy',
    region_label:           'Region',
    region_placeholder:     'Auto-filled from facility',
    arrival_time:           'Arrival Time',
    departure_time:         'Departure Time',
    comments_attitude:      'Comments (Attitude):',
    comments_supplies:      'Comments (Supplies):',
    comments_optional:      'Optional comments...',
    final_comments_title:   '💬 Any additional comments or suggestions?',
    final_comments_placeholder: 'Your suggestions help us improve...',
    med_not_received:       'Which medication(s) did you NOT receive?',
    review_answers_title:   '⚠ Please review your answers',
    review_answers_btn:     '← Review my answers',
    submit_anyway:          'Submit anyway',
    submitting:             '⏳ Submitting...',
    submit_complete:        'Submit My Feedback ✓',
    submit_incomplete:      'Answer all questions to submit',
    answer_all:             'Please answer all questions before submitting.',
    questions_unanswered:   'questions unanswered:',
    all_rows_required:      '* all rows required',
    required:               '* required',
    TIMELINESS:             'TIMELINESS',
    ATTITUDE:               'ATTITUDE',
    FACILITY:               'FACILITY',
    SUPPLIES:               'SUPPLIES',
    'PATIENT RIGHTS':       'PATIENT RIGHTS',
    OVERALL:                'OVERALL',
    inpatient_survey_title: '🛏 Inpatient Patient Satisfaction Survey',
    outpatient_survey_title:'🏢 Outpatient Patient Satisfaction Survey',
    moh_nqmp:               'Ministry of Health · National Quality Management Programme',
    back_btn:               '← Back',
    lang_toggle_en:         'English',
    lang_toggle_ss:         'SiSwati',
    // question texts
    'IP.1':  'After admission how long did you wait before being allocated a ward and bed?',
    'IP.2':  'During your stay, how was your interaction with health workers?',
    'IP.3':  'How is signage for directions to service points?',
    'IP.4':  'How is the facility cleanliness? (Toilets, showers, linen, ward)',
    'IP.5':  'Did you receive all the prescribed medication?',
    'IP.6':  'Did you receive your meals on time?',
    'IP.7':  'How was the presentation of the food?',
    'IP.8':  'Was your privacy respected during your stay?',
    'IP.9':  'Did the health practitioner explain your condition and treatment plan?',
    'IP.10': 'Were you aware you could make a formal complaint?',
    'IP.11': 'How satisfactory were the hospital routines and procedures?',
    'OP.1':  'How long did you wait before receiving service?',
    'OP.2':  'How would you rate the time management during service delivery?',
    'OP.3':  'How was the attitude of the health care provider?',
    'OP.4':  'How is signage for directions to service points?',
    'OP.5':  'How is the facility cleanliness?',
    'OP.6':  'Did you receive all the prescribed medication?',
    'OP.7':  'Were you treated with respect?',
    'OP.8':  'Did the health practitioner explain your condition and treatment plan?',
    'OP.9':  'Were you given clear guidance on follow up care?',
    'OP.10': 'How satisfied were you with the service you received?',
    // options
    'Very Satisfied':    'Very Satisfied',
    'Satisfied':         'Satisfied',
    'Unsatisfied':       'Unsatisfied',
    'Very Unsatisfied':  'Very Unsatisfied',
    'Very Good':         'Very Good',
    'Very good':         'Very good',
    'Good':              'Good',
    'good':              'good',
    'Average':           'Average',
    'Poor':              'Poor',
    'Very Poor':         'Very Poor',
    'Very poor':         'Very poor',
    'Yes':               'Yes',
    'No':                'No',
    '< 30 Minutes':      '< 30 Minutes',
    '30min-1 Hour':      '30min-1 Hour',
    '1 Hour-2 Hours':    '1 Hour-2 Hours',
    'More than 2 Hours': 'More than 2 Hours',
    // sub items
    'Nurse':      'Nurse',
    'Doctor':     'Doctor',
    'Caterers':   'Caterers',
    'Laboratory': 'Laboratory',
    'Pharmacy':   'Pharmacy',
    'Screening':  'Screening',
    // commodity groups
    'HIV':   'HIV',
    'MCH':   'MCH',
    'Other': 'Other',
    'ARVs — 1st line (TLD, TLE)':              'ARVs — 1st line (TLD, TLE)',
    'OI Prophylaxis (Cotrimoxazole)':           'OI Prophylaxis (Cotrimoxazole)',
    'TB treatment':                             'TB treatment',
    'Antenatal supplements (Iron, Folic Acid)': 'Antenatal supplements (Iron, Folic Acid)',
    'Family Planning commodities':              'Family Planning commodities',
    'PMTCT (Nevirapine)':                       'PMTCT (Nevirapine)',
    'Antibiotics':                              'Antibiotics',
    'Pain medication':                          'Pain medication',
    'Chronic medication (Diabetes/Hypertension)':'Chronic medication (Diabetes/Hypertension)',
    'Other medication':                         'Other medication',
};

const { TextArea } = Input;

// ── Questions ─────────────────────────────────────────────────────────────────
const IP_QUESTIONS = [
    { ref:'IP.1', section:'TIMELINESS', text:'After admission how long did you wait before being allocated a ward and bed?',
      type:'radio', options:['< 30 Minutes','30min-1 Hour','1 Hour-2 Hours','More than 2 Hours'] },
    { ref:'IP.2', section:'ATTITUDE', text:'During your stay, how was your interaction with health workers?',
      type:'matrix', sub_items:['Nurse','Doctor','Caterers','Laboratory','Pharmacy'],
      options:['Very good','Good','Poor','Very poor'] },
    { ref:'IP.3', section:'FACILITY', text:'How is signage for directions to service points?',
      type:'radio', options:['Very Good','Good','Average','Poor','Very Poor'] },
    { ref:'IP.4', section:'FACILITY', text:'How is the facility cleanliness? (Toilets, showers, linen, ward)',
      type:'radio', options:['Very Good','Good','Average','Poor','Very Poor'] },
    { ref:'IP.5', section:'SUPPLIES', text:'Did you receive all the prescribed medication?', type:'yesno' },
    { ref:'IP.6', section:'SUPPLIES', text:'Did you receive your meals on time?', type:'yesno' },
    { ref:'IP.7', section:'SUPPLIES', text:'How was the presentation of the food?',
      type:'radio', options:['Very good','Good','Average','Poor','Very Poor'] },
    { ref:'IP.8', section:'PATIENT RIGHTS', text:'Was your privacy respected during your stay?', type:'yesno' },
    { ref:'IP.9', section:'PATIENT RIGHTS', text:'Did the health practitioner explain your condition and treatment plan?', type:'yesno' },
    { ref:'IP.10', section:'PATIENT RIGHTS', text:'Were you aware you could make a formal complaint?', type:'yesno' },
    { ref:'IP.11', section:'OVERALL', text:'How satisfactory were the hospital routines and procedures?',
      type:'radio', options:['Very Satisfied','Satisfied','Unsatisfied','Very Unsatisfied'] },
];

const OP_QUESTIONS = [
    { ref:'OP.1', section:'TIMELINESS', text:'How long did you wait before receiving service?',
      type:'matrix', sub_items:['Screening','Nurse','Doctor','Laboratory','Pharmacy'],
      options:['< 30 Minutes','30min-1 Hour','1 Hour-2 Hours','More than 2 Hours'] },
    { ref:'OP.2', section:'TIMELINESS', text:'How would you rate the time management during service delivery?',
      type:'radio', options:['Very Good','Good','Average','Poor','Very Poor'] },
    { ref:'OP.3', section:'ATTITUDE', text:'How was the attitude of the health care provider?',
      type:'radio', options:['Very Good','Good','Average','Poor','Very Poor'] },
    { ref:'OP.4', section:'FACILITY', text:'How is signage for directions to service points?',
      type:'radio', options:['Very Good','Good','Average','Poor','Very Poor'] },
    { ref:'OP.5', section:'FACILITY', text:'How is the facility cleanliness?',
      type:'radio', options:['Very Good','Good','Average','Poor','Very Poor'] },
    { ref:'OP.6', section:'SUPPLIES', text:'Did you receive all the prescribed medication?', type:'yesno' },
    { ref:'OP.7', section:'PATIENT RIGHTS', text:'Were you treated with respect?', type:'yesno' },
    { ref:'OP.8', section:'PATIENT RIGHTS', text:'Did the health practitioner explain your condition and treatment plan?', type:'yesno' },
    { ref:'OP.9', section:'PATIENT RIGHTS', text:'Were you given clear guidance on follow up care?', type:'yesno' },
    { ref:'OP.10', section:'OVERALL', text:'How satisfied were you with the service you received?',
      type:'radio', options:['Very Satisfied','Satisfied','Unsatisfied','Very Unsatisfied'] },
];

const SECTION_COLORS = {
    TIMELINESS:'#0ea5e9', ATTITUDE:'#8b5cf6', FACILITY:'#10b981',
    SUPPLIES:'#f59e0b', 'PATIENT RIGHTS':'#ef4444', OVERALL:'#6366f1',
};

const COMMODITY_GROUPS = [
    { key:'HIV',   label:'HIV',   items:['ARVs — 1st line (TLD, TLE)','OI Prophylaxis (Cotrimoxazole)','TB treatment'] },
    { key:'MCH',   label:'MCH',   items:['Antenatal supplements (Iron, Folic Acid)','Family Planning commodities','PMTCT (Nevirapine)'] },
    { key:'OTHER', label:'Other', items:['Antibiotics','Pain medication','Chronic medication (Diabetes/Hypertension)','Other medication'] },
];

const SCORE_MAP = {
    'Very Satisfied':100,'Satisfied':75,'Unsatisfied':25,'Very Unsatisfied':0,
    'Very Good':100,'Very good':100,'Good':75,'good':75,
    'Average':50,'Poor':25,'Very Poor':0,'Very poor':0,
    'Yes':100,'No':0,
    '< 30 Minutes':100,'30min-1 Hour':75,'1 Hour-2 Hours':25,'More than 2 Hours':0,
};

const API_BASE = `${window.location.protocol}//${window.location.host}/qoc/api/`;

// ── Returns translated versions of IP/OP question arrays ─────────────────────
function getTranslatedQuestions(baseQuestions, t) {
    return baseQuestions.map(q => ({
        ...q,
        text:     t(q.ref) || q.text,
        options:  q.options  ? q.options.map(o  => t(o)  || o)  : undefined,
        sub_items:q.sub_items? q.sub_items.map(s => t(s)  || s)  : undefined,
    }));
}

// ── Device fingerprint ────────────────────────────────────────────────────────
function getDeviceId() {
    let id = localStorage.getItem('qoc_device_id');
    if (!id) {
        const nav = window.navigator;
        const seed = [
            nav.userAgent, nav.language, nav.hardwareConcurrency,
            window.screen.width, window.screen.height, window.screen.colorDepth,
            new Date().getTimezoneOffset(),
            nav.platform || '',
        ].join('|');
        let hash = 0;
        for (let i = 0; i < seed.length; i++) {
            hash = ((hash << 5) - hash) + seed.charCodeAt(i);
            hash |= 0;
        }
        id = 'dev_' + Math.abs(hash).toString(36) + '_' + Date.now().toString(36);
        localStorage.setItem('qoc_device_id', id);
    }
    return id;
}

// ── Submission lock helpers ───────────────────────────────────────────────────
function getSubmissionKey(facilityId, surveyType) {
    const year = new Date().getFullYear();
    return `qoc_submitted_${facilityId}_${surveyType}_${year}`;
}
function hasSubmittedThisYear(facilityId, surveyType) {
    if (!facilityId) return false;
    return !!localStorage.getItem(getSubmissionKey(facilityId, surveyType));
}
function markSubmitted(facilityId, surveyType) {
    localStorage.setItem(getSubmissionKey(facilityId, surveyType), JSON.stringify({
        timestamp:   new Date().toISOString(),
        survey_type: surveyType,
        device_id:   getDeviceId(),
    }));
}

// ── QR token verification ─────────────────────────────────────────────────────
const QR_SECRET = 'qoc-moh-sz-2025';

function haversineMetres(lat1, lon1, lat2, lon2) {
    const R = 6371000;
    const toRad = x => x * Math.PI / 180;
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a = Math.sin(dLat/2) ** 2 +
              Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon/2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function checkGeolocation(facLat, facLng, maxMetres = 100) {
    return new Promise((resolve) => {
        if (!facLat || !facLng) {
            resolve({ allowed: true, distance: null, error: null, skipped: true });
            return;
        }
        if (!navigator.geolocation) {
            resolve({ allowed: false, distance: null, error: 'no_geolocation' });
            return;
        }
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                const dist = Math.round(haversineMetres(
                    pos.coords.latitude, pos.coords.longitude,
                    parseFloat(facLat), parseFloat(facLng)
                ));
                resolve({ allowed: dist <= maxMetres, distance: dist, error: null });
            },
            (err) => {
                resolve({ allowed: false, distance: null,
                    error: err.code === 1 ? 'denied' : 'unavailable' });
            },
            { timeout: 10000, maximumAge: 60000, enableHighAccuracy: true }
        );
    });
}

async function sha256(message) {
    if (!window.crypto?.subtle) return '';
    const msgBuffer = new TextEncoder().encode(message);
    const hashBuffer = await window.crypto.subtle.digest('SHA-256', msgBuffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

async function verifyQrToken(facCode, timestamp, token) {
    if (!facCode || !timestamp || !token) return false;
    const age = Date.now() - parseInt(timestamp) * 1000;
    if (age > 365 * 24 * 60 * 60 * 1000) return false;
    return true;
}

function getQrParams() {
    const params = new URLSearchParams(window.location.search);
    return {
        facCode:   params.get('fac')  || '',
        token:     params.get('tok')  || '',
        timestamp: params.get('t')    || '',
        region:    params.get('region') || params.get('reg') || '',
        facLat:    params.get('lat')  || '',
        facLng:    params.get('lng')  || '',
    };
}

// ── Consistency validator ─────────────────────────────────────────────────────
function validateConsistency(resp, questions, isIP) {
    const warnings = [];
    const getVal = (ref) => resp[ref] || '';

    const overallRef = isIP ? 'IP.11' : 'OP.10';
    const overall = getVal(overallRef);
    const negativeCount = questions.filter(q => {
        if (q.type === 'radio') {
            const v = getVal(q.ref);
            return ['Poor','Very Poor','Very poor','Unsatisfied','Very Unsatisfied'].includes(v);
        }
        if (q.type === 'yesno') return getVal(q.ref) === 'No';
        return false;
    }).length;

    if (overall === 'Very Satisfied' && negativeCount >= 3) {
        warnings.push('Overall satisfaction is Very Satisfied but several areas rated poor — please review your answers.');
    }
    if (overall === 'Very Unsatisfied' && negativeCount === 0) {
        warnings.push('Overall satisfaction is Very Unsatisfied but no issues were reported — please review your answers.');
    }

    const matrixQ = questions.find(q => q.type === 'matrix');
    if (matrixQ) {
        const matrixVals = matrixQ.sub_items.map(s => resp[`${matrixQ.ref}__${s}`]).filter(Boolean);
        if (matrixVals.length === matrixQ.sub_items.length) {
            const allSame = matrixVals.every(v => v === matrixVals[0]);
            if (allSame && matrixVals[0] === 'Very good') {
                warnings.push('All staff interactions rated identically — please rate each cadre based on your actual experience.');
            }
        }
    }

    const startTime = parseInt(sessionStorage.getItem('qoc_form_start') || '0');
    if (startTime > 0) {
        const elapsed = (Date.now() - startTime) / 1000;
        if (elapsed < 90 && negativeCount === 0 && overall === 'Very Satisfied') {
            warnings.push('Form completed very quickly with all perfect scores. Please take time to reflect on your actual experience.');
        }
    }

    return warnings;
}

// ── Draft helpers ─────────────────────────────────────────────────────────────
const DRAFT_KEY = 'qoc_survey_draft';
function saveDraft(data) {
    try { localStorage.setItem(DRAFT_KEY, JSON.stringify({ ...data, saved_at: new Date().toISOString() })); }
    catch(e) {}
}
function loadDraft() {
    try { return JSON.parse(localStorage.getItem(DRAFT_KEY) || 'null'); }
    catch(e) { return null; }
}
function clearDraft() {
    localStorage.removeItem(DRAFT_KEY);
}

// ── Shared UI atom ────────────────────────────────────────────────────────────
function OptionBtn({ label, selected, onClick, color }) {
    const good = ['Very Good','Very good','Very Satisfied','Yes','< 30 Minutes'].includes(label);
    const bad  = ['Very Poor','Very poor','Very Unsatisfied','No','More than 2 Hours'].includes(label);
    const bg   = selected ? (color || (good ? '#52c41a' : bad ? '#f5222d' : '#1890ff')) : '#f5f5f5';
    return (
        <button onClick={onClick} style={{
            padding:'8px 16px', borderRadius:8, margin:'4px',
            border: selected ? 'none' : '1px solid #ddd',
            background: bg, color: selected ? '#fff' : '#444',
            cursor:'pointer', fontSize:14, fontWeight: selected ? 700 : 400,
            transition:'all 0.15s',
        }}>{label}</button>
    );
}

// ── Helpers ───────────────────────────────────────────────────────────────────
// Parse services_offered — handles both JSON array and plain array
function parseServices(raw) {
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    try { return JSON.parse(raw); } catch { return []; }
}

// ── Landing page — patient chooses survey type ────────────────────────────────
function SurveyLanding({ onSelect, qrValid, qrFacCode, qrRegion, qrFacName, geoStatus, geoDistance, facServices, facSiteType, lang, setLang, t = k => k }) {
    if (!qrValid) {
        return (
            <div style={{ minHeight:'100vh', background:'#fff1f0',
                display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
                <div style={{ maxWidth:440, textAlign:'center', background:'#fff',
                    borderRadius:16, padding:'48px 32px',
                    boxShadow:'0 8px 40px rgba(0,0,0,0.1)' }}>
                    <div style={{ fontSize:56, marginBottom:16 }}>⛔</div>
                    <div style={{ fontSize:20, fontWeight:800, marginBottom:12, color:'#cf1322' }}>
                        {t('invalid_access_title')}
                    </div>
                    <div style={{ color:'#666', fontSize:14, lineHeight:1.7, marginBottom:20 }}>
                        {t('invalid_access_body')}
                    </div>
                    <div style={{ fontSize:12, color:'#aaa' }}>
                        {t('moh_footer')}
                    </div>
                </div>
            </div>
        );
    }

    // ── Determine available survey types based on facility services ───────────
    // If services_offered is not configured, default to showing both options.
    // 'Inpatient' in services_offered controls whether the inpatient card appears.
    const services = parseServices(facServices);
    // Inpatient only for Clinic with Maternity and above
    // Outpatient only: Clinic without Maternity, Specialized Clinic, Public Health Unit
    // Inpatient + Outpatient: everything else
    const outpatientOnlyTypes = ['Clinic without Maternity', 'Specialized Clinic', 'Public Health Unit'];
    const showInpatient  = !outpatientOnlyTypes.includes(facSiteType)
                        || services.includes('Inpatient');
    const showOutpatient = true;

    const SURVEY_TYPES = [
        showInpatient  && { key:'inpatient',  emoji:'🛏',  label:t('inpatient_label'),  color:'#003580', desc:t('inpatient_desc') },
        showOutpatient && { key:'outpatient', emoji:'🏢',  label:t('outpatient_label'), color:'#006837', desc:t('outpatient_desc') },
    ].filter(Boolean);

    return (
        <div style={{ minHeight:'100vh', background:'linear-gradient(135deg,#003580 0%,#006837 100%)',
            display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
            <div style={{ maxWidth:520, width:'100%' }}>
                {/* Header */}
                {/* Language toggle */}
                <div style={{ textAlign:'right', marginBottom:8 }}>
                    <button onClick={() => setLang(l => l === 'en' ? 'ss' : 'en')}
                        style={{ background:'rgba(255,255,255,0.2)', border:'1px solid rgba(255,255,255,0.4)',
                            color:'#fff', borderRadius:20, padding:'5px 14px', cursor:'pointer',
                            fontSize:12, fontWeight:600, letterSpacing:0.5 }}>
                        {lang === 'en' ? t('lang_toggle_ss') : t('lang_toggle_en')}
                    </button>
                </div>
                <div style={{ textAlign:'center', marginBottom:32, color:'#fff' }}>
                    <div style={{ fontSize:48, marginBottom:12 }}>🏥</div>
                    <div style={{ fontSize:11, letterSpacing:3, opacity:0.8, marginBottom:6, textTransform:'uppercase' }}>
                        {t('moh_label')}
                    </div>
                    <div style={{ fontSize:24, fontWeight:800, marginBottom:8 }}>
                        {t('survey_title')}
                    </div>
                    {qrFacName && (
                        <div style={{ background:'rgba(255,255,255,0.15)', borderRadius:8,
                            padding:'8px 16px', marginBottom:8, fontSize:14, fontWeight:700 }}>
                            📍 {qrFacName}
                            {qrRegion && <span style={{ opacity:0.8, fontWeight:400 }}> · {qrRegion}</span>}
                        </div>
                    )}
                    {geoStatus === 'ok' && (
                        <div style={{ display:'inline-flex', alignItems:'center', gap:6,
                            background:'rgba(82,196,26,0.2)', borderRadius:20,
                            padding:'4px 14px', fontSize:12, marginBottom:8 }}>
                            <span style={{ width:8, height:8, borderRadius:'50%',
                                background:'#52c41a', display:'inline-block' }}/>
                            {t('moh_nqmp')} · {geoDistance}m
                        </div>
                    )}
                    <div style={{ fontSize:14, opacity:0.85, lineHeight:1.6 }}>
                        {t('nqmp_subtitle').split('\n').map((line, i) => <span key={i}>{line}{i === 0 && <br/>}</span>)}
                    </div>
                </div>

                {/* Choice cards — filtered by facility services */}
                <div style={{ fontSize:14, color:'rgba(255,255,255,0.8)', textAlign:'center', marginBottom:16, fontWeight:600 }}>
                    {t('select_visit_type')}
                </div>
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: SURVEY_TYPES.length === 1 ? '1fr' : '1fr 1fr',
                    gap: 16,
                    marginBottom: 24,
                    maxWidth: SURVEY_TYPES.length === 1 ? 300 : '100%',
                    marginLeft: 'auto',
                    marginRight: 'auto',
                }}>
                    {SURVEY_TYPES.map(st => (
                        <button key={st.key} onClick={() => onSelect(st.key)} style={{
                            background:'rgba(255,255,255,0.95)', borderRadius:16, padding:'28px 20px',
                            border:'3px solid transparent', cursor:'pointer', transition:'all 0.2s',
                            textAlign:'center', boxShadow:'0 8px 32px rgba(0,0,0,0.2)',
                        }}
                            onMouseEnter={e => { e.currentTarget.style.transform='translateY(-4px)'; e.currentTarget.style.borderColor=st.color; }}
                            onMouseLeave={e => { e.currentTarget.style.transform='none'; e.currentTarget.style.borderColor='transparent'; }}>
                            <div style={{ fontSize:44, marginBottom:10 }}>{st.emoji}</div>
                            <div style={{ fontWeight:800, fontSize:17, color:st.color, marginBottom:6 }}>{st.label}</div>
                            <div style={{ fontSize:12, color:'#666', lineHeight:1.5 }}>{st.desc}</div>
                        </button>
                    ))}
                </div>

                <div style={{ textAlign:'center', fontSize:11, color:'rgba(255,255,255,0.6)' }}>
                    {t('anonymous_note')}
                </div>
            </div>
        </div>
    );
}

// ── Main survey form ───────────────────────────────────────────────────────────
function SurveyForm({ type, onBack, qrFacCode, qrRegion, qrFacName, qrFacId, qrFacLat, qrFacLng, lang, setLang, t = k => k }) {
    const isIP     = type === 'inpatient';
    const questions = getTranslatedQuestions(isIP ? IP_QUESTIONS : OP_QUESTIONS, t);
    const sections  = [...new Set(questions.map(q => q.section))];
    const accent    = isIP ? '#003580' : '#006837';
    const accentLight = isIP ? '#0050b3' : '#00a651';

    const [facilities, setFacilities] = useState([]);
    const [loadingFacs, setLoadingFacs] = useState(true);
    const [periodLabel, setPeriodLabel] = useState('');
    const [facilityId, setFacilityId]   = useState(null);
    const [facilityName, setFacilityName] = useState('');
    const [region, setRegion]           = useState('');
    const [resp, setRespState]          = useState({});
    const [comments, setComments]       = useState('');
    const [notReceived, setNotReceived] = useState({});
    const [submitting, setSubmitting]   = useState(false);
    const [submitted, setSubmitted]     = useState(false);
    const [alreadySubmitted, setAlreadySubmitted] = useState(false);
    const [patientGeo,       setPatientGeo]       = useState(null);
    const [draftRestored,    setDraftRestored]    = useState(false);
    const [consistencyWarnings, setConsistencyWarnings] = useState([]);
    const [showWarnings,        setShowWarnings]        = useState(false);
    const autoSaveTimer = useRef(null);

    const [wardAdmitted,       setWardAdmitted]       = useState('');
    const [departmentsVisited, setDepartmentsVisited] = useState('');
    const [arrivalTime,        setArrivalTime]        = useState('');
    const [departureTime,      setDepartureTime]      = useState('');
    const [regionManual,       setRegionManual]       = useState('');
    const [attitudeComments,   setAttitudeComments]   = useState('');
    const [suppliesComments,   setSuppliesComments]   = useState('');
    const [patientGender,  setPatientGender]  = useState('');
    const [patientAge,     setPatientAge]     = useState('');
    const [paidForService, setPaidForService] = useState('');
    const [amountPaid,     setAmountPaid]     = useState('');

    useEffect(() => {
        const draft = loadDraft();
        if (draft && draft.survey_type === type && draft.resp && Object.keys(draft.resp).length > 0) {
            setRespState(draft.resp || {});
            setNotReceived(draft.notReceived || {});
            setFacilityId(draft.facilityId || null);
            setFacilityName(draft.facilityName || '');
            setRegionManual(draft.regionManual || '');
            setPatientGender(draft.patientGender || '');
            setPatientAge(draft.patientAge || '');
            setPaidForService(draft.paidForService || '');
            setAmountPaid(draft.amountPaid || '');
            setComments(draft.comments || '');
            setWardAdmitted(draft.wardAdmitted || '');
            setDepartmentsVisited(draft.departmentsVisited || '');
            setDraftRestored(true);
        }

        axios.get(`${API_BASE}qoc/public/facilities`)
            .then(r => {
                const facs = r.data?.data || [];
                setFacilities(facs);
                if (qrFacId || qrFacCode) {
                    const match = facs.find(f =>
                        (qrFacId && String(f.id) === String(qrFacId)) ||
                        (qrFacCode && (f.fac_code === qrFacCode || f.business_name === qrFacName))
                    );
                    if (match) {
                        setFacilityId(match.id);
                        setFacilityName(match.business_name);
                        setRegionManual(match.located_region || match.region || qrRegion || '');
                    } else if (qrFacName) {
                        setFacilityName(qrFacName);
                        setRegionManual(qrRegion || '');
                    }
                }
            })
            .catch(() => setFacilities([]))
            .finally(() => setLoadingFacs(false));

        axios.get(`${API_BASE}qoc/public/periods`)
            .then(r => {
                const periods = r.data?.data || [];
                const today   = moment().format('YYYY-MM-DD');
                const match   = periods.find(p => p.period_start && p.period_end &&
                    today >= p.period_start && today <= p.period_end);
                const current = periods.find(p => parseInt(p.is_current) === 1);
                const chosen  = match || current || periods[0];
                if (chosen) setPeriodLabel(chosen.label);
            })
            .catch(() => {});

        sessionStorage.setItem('qoc_form_start', String(Math.floor(Date.now() / 1000)));
        return () => { if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current); };
    }, []);

    const setR = (ref, val, sub=null) => {
        const k = sub ? `${ref}__${sub}` : ref;
        setRespState(p => ({ ...p, [k]: val }));
    };
    const getR = (ref, sub=null) => resp[sub ? `${ref}__${sub}` : ref];

    useEffect(() => {
        if (Object.keys(resp).length === 0) return;
        if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
        autoSaveTimer.current = setTimeout(() => {
            saveDraft({
                survey_type: type, resp, notReceived,
                facilityId, facilityName, regionManual,
                patientGender, patientAge, paidForService, amountPaid,
                comments, wardAdmitted, departmentsVisited,
                qrFacCode, qrFacName, qrFacId, qrRegion, qrFacLat, qrFacLng,
            });
        }, 3000);
    }, [resp, facilityId, patientGender, patientAge, paidForService, amountPaid, comments]);

    const completion = (() => {
        let tot=0, done=0;
        questions.forEach(q => {
            if (q.type==='matrix') q.sub_items.forEach(s => { tot++; if (getR(q.ref,s)) done++; });
            else { tot++; if (getR(q.ref)) done++; }
        });
        return tot ? Math.round(done/tot*100) : 0;
    })();

    const buildPayload = () => {
        const arr = [];
        questions.forEach(q => {
            if (q.type==='matrix') {
                q.sub_items.forEach(s => {
                    const v = getR(q.ref,s);
                    if (v) arr.push({ question_ref:q.ref, sub_item:s, response_value:v });
                });
            } else {
                const v = getR(q.ref);
                if (v) arr.push({ question_ref:q.ref, sub_item:null, response_value:v });
                if (v === 'No' && (q.ref === 'IP.5' || q.ref === 'OP.6')) {
                    const nr = notReceived[q.ref] || [];
                    COMMODITY_GROUPS.forEach(grp => {
                        const selected = grp.items.filter(item => nr.includes(item));
                        if (selected.length > 0) {
                            arr.push({
                                question_ref: `${q.ref}__notreceived__${grp.key}`,
                                sub_item: grp.label,
                                response_value: selected.join('|'),
                            });
                        }
                    });
                }
            }
        });
        return arr;
    };

    const handleSubmit = async () => {
        const unanswered = [];
        questions.forEach(q => {
            if (q.type === 'matrix') {
                q.sub_items.forEach(sub => { if (!getR(q.ref, sub)) unanswered.push(`${q.ref} (${sub})`); });
            } else {
                if (!getR(q.ref)) unanswered.push(q.ref);
            }
        });
        if (unanswered.length > 0) {
            message.warning({
                content: (
                    <div>
                        <div style={{ fontWeight:700, marginBottom:4 }}>Please answer all questions before submitting.</div>
                        <div style={{ fontSize:12, color:'#888' }}>
                            {unanswered.length} question{unanswered.length !== 1 ? 's' : ''} unanswered:&nbsp;
                            {unanswered.slice(0, 6).join(', ')}{unanswered.length > 6 ? '...' : ''}
                        </div>
                    </div>
                ),
                duration: 5,
            });
            const firstRef = unanswered[0].split(' ')[0];
            const el = document.querySelector(`[data-qref="${firstRef}"]`);
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return;
        }
        if (!facilityId) { message.error('Please select your facility'); return; }
        if (hasSubmittedThisYear(facilityId, type)) { setAlreadySubmitted(true); return; }

        const warnings = validateConsistency(resp, questions, isIP);
        if (warnings.length > 0 && !showWarnings) {
            setConsistencyWarnings(warnings);
            setShowWarnings(true);
            return;
        }
        setShowWarnings(false);
        setSubmitting(true);

        try {
            if (navigator.geolocation) {
                navigator.geolocation.getCurrentPosition(
                    pos => setPatientGeo({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
                    () => {},
                    { timeout: 3000, maximumAge: 30000 }
                );
            }
        } catch(e) {}

        try {
            const responses    = buildPayload();
            const scores       = responses.map(r => SCORE_MAP[r.response_value] ?? null).filter(s => s !== null);
            const overallScore = scores.length > 0
                ? parseFloat((scores.reduce((a,b) => a+b, 0) / scores.length).toFixed(1)) : 0;

            await axios.post(`${API_BASE}qoc/public/survey`, {
                facility_id:         facilityId,
                facility_name:       facilityName,
                region:              regionManual || region,
                survey_type:         type,
                survey_date:         moment().format('YYYY-MM-DD'),
                period_label:        periodLabel,
                overall_score:       overallScore,
                comments,
                ward_admitted:       isIP ? wardAdmitted : '',
                departments_visited: isIP ? '' : departmentsVisited,
                arrival_time:        arrivalTime,
                departure_time:      departureTime,
                attitude_comments:   attitudeComments,
                supplies_comments:   suppliesComments,
                patient_gender:      patientGender,
                patient_age:         patientAge,
                paid_for_service:    paidForService,
                amount_paid:         paidForService === 'Yes' ? amountPaid : '',
                responses,
                submitted_via:       'public_link',
                device_id:           getDeviceId(),
                qr_fac_code:         qrFacCode  || '',
                qr_timestamp:        new URLSearchParams(window.location.search).get('t') || '',
                qr_token:            new URLSearchParams(window.location.search).get('tok') || '',
                patient_lat:         patientGeo?.lat || null,
                patient_lng:         patientGeo?.lng || null,
            });
            markSubmitted(facilityId, type);
            clearDraft();
            setSubmitted(true);
        } catch (e) {
            message.error('Failed to submit. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    if (submitted) {
        return (
            <div style={{ minHeight:'100vh', background:'#f0f5ff',
                display:'flex', alignItems:'center', justifyContent:'center' }}>
                <div style={{ maxWidth:480, textAlign:'center', background:'#fff',
                    borderRadius:16, padding:'48px 32px', boxShadow:'0 8px 40px rgba(0,0,0,0.1)' }}>
                    <CheckCircleOutlined style={{ fontSize:64, color:'#52c41a', display:'block', marginBottom:16 }}/>
                    <div style={{ fontSize:24, fontWeight:800, marginBottom:8 }}>{t('thank_you_title')}</div>
                    <div style={{ color:'#666', marginBottom:24, lineHeight:1.6 }}>
                        {t('thank_you_body').split('\n').map((line, i) => <span key={i}>{line}{i === 0 && <br/>}</span>)}
                    </div>
                    <div style={{ fontSize:13, color:'#888', marginTop:8,
                        background:'#f0f5ff', borderRadius:8, padding:'10px 16px',
                        border:'1px solid #d0e4ff' }}>
                        🔒 You can submit feedback for this facility again after 12 months.
                    </div>
                    <div style={{ fontSize:11, color:'#aaa', marginTop:20 }}>
                        {t('moh_footer')}
                    </div>
                </div>
            </div>
        );
    }

    if (alreadySubmitted) {
        return (
            <div style={{ minHeight:'100vh', background:'#fff7e6',
                display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
                <div style={{ maxWidth:480, textAlign:'center', background:'#fff',
                    borderRadius:16, padding:'48px 32px', boxShadow:'0 8px 40px rgba(0,0,0,0.1)' }}>
                    <div style={{ fontSize:56, marginBottom:16 }}>🔒</div>
                    <div style={{ fontSize:22, fontWeight:800, marginBottom:8, color:'#d46b08' }}>{t('already_submitted_title')}</div>
                    <div style={{ color:'#666', marginBottom:24, lineHeight:1.6, fontSize:14 }}>
                        {t('already_submitted_body')}
                    </div>
                    <button onClick={onBack} style={{
                        background:'#d46b08', color:'#fff', border:'none',
                        borderRadius:8, padding:'10px 24px', cursor:'pointer', fontSize:14, fontWeight:700,
                    }}>{t('go_back')}</button>
                </div>
            </div>
        );
    }

    return (
        <div style={{ minHeight:'100vh', background:'#f0f5ff', fontFamily:'Arial,sans-serif' }}>
            {/* Header */}
            <div style={{ background:`linear-gradient(135deg,${accent},${accentLight})`, padding:'14px 20px', color:'#fff' }}>
                <div style={{ maxWidth:680, margin:'0 auto', display:'flex', alignItems:'center', gap:12 }}>
                    <button onClick={onBack} style={{
                        background:'rgba(255,255,255,0.2)', border:'none', color:'#fff',
                        borderRadius:8, padding:'6px 12px', cursor:'pointer', fontSize:13,
                        display:'flex', alignItems:'center', gap:4,
                    }}>{t('back_btn')}</button>
                    <div style={{ flex:1, textAlign:'center' }}>
                        <div style={{ fontSize:18, fontWeight:800 }}>
                            {isIP ? t('inpatient_survey_title') : t('outpatient_survey_title')}
                        </div>
                        <div style={{ fontSize:11, opacity:0.85, marginTop:2 }}>
                            {t('moh_nqmp')}
                        </div>
                    </div>
                    <button onClick={() => setLang(l => l === 'en' ? 'ss' : 'en')}
                        style={{ background:'rgba(255,255,255,0.2)', border:'1px solid rgba(255,255,255,0.4)',
                            color:'#fff', borderRadius:20, padding:'4px 12px', cursor:'pointer',
                            fontSize:11, fontWeight:600, whiteSpace:'nowrap', flexShrink:0 }}>
                        {lang === 'en' ? t('lang_toggle_ss') : t('lang_toggle_en')}
                    </button>
                </div>
            </div>

            <div style={{ maxWidth:680, margin:'0 auto', padding:'16px' }}>

                {/* Draft restored banner */}
                {draftRestored && (
                    <div style={{ background:'#e6f7ff', border:'1px solid #91d5ff',
                        borderRadius:12, padding:'12px 16px', marginBottom:14,
                        display:'flex', alignItems:'center', justifyContent:'space-between', gap:12 }}>
                        <div>
                            <div style={{ fontWeight:700, fontSize:13, color:'#1677ff' }}>{t('draft_restored_title')}</div>
                            <div style={{ fontSize:12, color:'#555', marginTop:2 }}>
                                {t('draft_restored_body')}
                            </div>
                        </div>
                        <button onClick={() => { clearDraft(); window.location.reload(); }}
                            style={{ background:'none', border:'1px solid #91d5ff', borderRadius:6,
                                padding:'4px 10px', cursor:'pointer', fontSize:12, color:'#1677ff',
                                whiteSpace:'nowrap', flexShrink:0 }}>
                            {t('start_fresh')}
                        </button>
                    </div>
                )}

                {/* Progress */}
                <div style={{ background:'#fff', borderRadius:12, padding:'12px 16px', marginBottom:14 }}>
                    <div style={{ display:'flex', justifyContent:'space-between', marginBottom:6 }}>
                        <span style={{ fontSize:12, color:'#888' }}>{t('completion_label')}</span>
                        <span style={{ fontSize:13, fontWeight:700,
                            color: completion===100 ? '#52c41a' : completion>=50 ? '#fa8c16' : '#f5222d' }}>{completion}%</span>
                    </div>
                    <Progress percent={completion} showInfo={false}
                        strokeColor={completion===100 ? '#52c41a' : completion>=50 ? '#fa8c16' : '#f5222d'} size="small"/>
                </div>

                {/* Facility */}
                <div style={{ background:'#fff', borderRadius:12, padding:16, marginBottom:14 }}>
                    <div style={{ fontWeight:700, marginBottom:8, fontSize:14 }}>{t('select_facility')}</div>
                    {loadingFacs
                        ? <Spin indicator={<LoadingOutlined spin/>} size="small"/>
                        : <Select showSearch optionFilterProp="children"
                            placeholder={t('search_facility')}
                            style={{ width:'100%', fontSize:14 }}
                            value={facilityId}
                            disabled={!!(qrFacId || qrFacCode)}
                            onChange={(v) => {
                                setFacilityId(v);
                                const fac = facilities.find(f => f.id === v);
                                setFacilityName(fac?.business_name || '');
                                const r = fac?.located_region || fac?.region || '';
                                setRegion(r); setRegionManual(r);
                            }}>
                            {facilities.map(f => (
                                <Select.Option key={f.id} value={f.id}>
                                    {f.business_name}
                                    {f.located_region && <span style={{ color:'#aaa', marginLeft:6, fontSize:12 }}>· {f.located_region}</span>}
                                </Select.Option>
                            ))}
                        </Select>
                    }
                </div>

                {/* Patient details */}
                <div style={{ background:'#fff', borderRadius:12, padding:16, marginBottom:14 }}>
                    <div style={{ fontWeight:700, fontSize:13, marginBottom:12 }}>{t('patient_details')}</div>
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                        <div>
                            <label style={{ display:'block', fontSize:12, fontWeight:600, marginBottom:4, color:'#555' }}>{t('gender_label')}</label>
                            <div style={{ display:'flex', gap:8 }}>
                                {[t('gender_male'),t('gender_female'),t('gender_other')].map(g => (
                                    <button key={g} onClick={() => setPatientGender(g)} style={{
                                        flex:1, padding:'8px 4px', borderRadius:8, fontSize:13,
                                        border: patientGender===g ? 'none' : '1px solid #ddd',
                                        background: patientGender===g ? accent : '#f5f5f5',
                                        color: patientGender===g ? '#fff' : '#444',
                                        cursor:'pointer', fontWeight: patientGender===g ? 700 : 400,
                                    }}>{g}</button>
                                ))}
                            </div>
                        </div>
                        <div>
                            <label style={{ display:'block', fontSize:12, fontWeight:600, marginBottom:4, color:'#555' }}>{t('age_label')}</label>
                            <input type="number" min="0" max="120" value={patientAge}
                                onChange={e => setPatientAge(e.target.value)}
                                placeholder={t('age_placeholder')}
                                style={{ width:'100%', padding:'8px 12px', border:'1px solid #d9d9d9',
                                    borderRadius:8, fontSize:14, boxSizing:'border-box' }}/>
                        </div>
                    </div>
                    <div style={{ marginTop:12 }}>
                        <label style={{ display:'block', fontSize:12, fontWeight:600, marginBottom:6, color:'#555' }}>
                            {t('paid_question')}
                        </label>
                        <div style={{ display:'flex', gap:8, marginBottom: paidForService === 'Yes' ? 10 : 0 }}>
                            {[t('paid_yes'),t('paid_no')].map(v => (
                                <button key={v} onClick={() => setPaidForService(v)} style={{
                                    padding:'8px 24px', borderRadius:8, fontSize:14,
                                    border: paidForService===v ? 'none' : '1px solid #ddd',
                                    background: paidForService===v ? (v==='Yes' ? '#f59e0b' : '#52c41a') : '#f5f5f5',
                                    color: paidForService===v ? '#fff' : '#444',
                                    cursor:'pointer', fontWeight: paidForService===v ? 700 : 400,
                                }}>{v}</button>
                            ))}
                        </div>
                        {paidForService === 'Yes' && (
                            <div>
                                <label style={{ display:'block', fontSize:12, fontWeight:600, marginBottom:4, color:'#555' }}>
                                    {t('amount_paid_label')}
                                </label>
                                <input type="number" min="0" value={amountPaid}
                                    onChange={e => setAmountPaid(e.target.value)}
                                    placeholder={t('amount_paid_placeholder')}
                                    style={{ width:180, padding:'8px 12px', border:'1px solid #d9d9d9', borderRadius:8, fontSize:14 }}/>
                            </div>
                        )}
                    </div>
                </div>

                {/* Visit details */}
                <div style={{ background:'#fff', borderRadius:12, padding:16, marginBottom:14 }}>
                    <div style={{ fontWeight:700, fontSize:13, marginBottom:12 }}>{t('visit_details')}</div>
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                        <div>
                            <label style={{ display:'block', fontSize:12, fontWeight:600, marginBottom:4, color:'#555' }}>
                                {isIP ? t('ward_admitted') : t('depts_visited')}
                            </label>
                            <input value={isIP ? wardAdmitted : departmentsVisited}
                                onChange={e => isIP ? setWardAdmitted(e.target.value) : setDepartmentsVisited(e.target.value)}
                                placeholder={isIP ? t('ward_placeholder') : t('depts_placeholder')}
                                style={{ width:'100%', padding:'7px 10px', border:'1px solid #d9d9d9', borderRadius:6, fontSize:13, boxSizing:'border-box' }}/>
                        </div>
                        <div>
                            <label style={{ display:'block', fontSize:12, fontWeight:600, marginBottom:4, color:'#555' }}>{t('region_label')}</label>
                            <input value={regionManual || region}
                                onChange={e => setRegionManual(e.target.value)}
                                placeholder={t('region_placeholder')}
                                readOnly={!!(qrRegion || region)}
                                style={{ width:'100%', padding:'7px 10px', border:'1px solid #d9d9d9', borderRadius:6, fontSize:13, boxSizing:'border-box',
                                    background: (qrRegion || region) ? '#f5f5f5' : '#fff' }}/>
                        </div>
                        <div>
                            <label style={{ display:'block', fontSize:12, fontWeight:600, marginBottom:4, color:'#555' }}>{t('arrival_time')}</label>
                            <input type="time" value={arrivalTime} onChange={e => setArrivalTime(e.target.value)}
                                style={{ width:'100%', padding:'7px 10px', border:'1px solid #d9d9d9', borderRadius:6, fontSize:13, boxSizing:'border-box' }}/>
                        </div>
                        <div>
                            <label style={{ display:'block', fontSize:12, fontWeight:600, marginBottom:4, color:'#555' }}>{t('departure_time')}</label>
                            <input type="time" value={departureTime} onChange={e => setDepartureTime(e.target.value)}
                                style={{ width:'100%', padding:'7px 10px', border:'1px solid #d9d9d9', borderRadius:6, fontSize:13, boxSizing:'border-box' }}/>
                        </div>
                    </div>
                </div>

                {/* Questions */}
                {sections.map(section => {
                    const qs     = questions.filter(q => q.section === section);
                    const sColor = SECTION_COLORS[section] || '#1890ff';
                    return (
                        <div key={section}>
                            <div style={{ background:sColor, color:'#fff', fontWeight:700, fontSize:13,
                                padding:'8px 16px', borderRadius:'10px 10px 0 0', marginTop:12 }}>
                                {t(section) || section}
                            </div>
                            <div style={{ background:'#fff', borderRadius:'0 0 10px 10px', padding:'12px 16px', marginBottom:4 }}>
                                {qs.map(q => {
                                    const qNum = q.ref.split('.')[1];

                                    if (q.type === 'matrix') {
                                        const matrixUnanswered = q.sub_items.some(s => !getR(q.ref, s));
                                        return (
                                            <div key={q.ref} data-qref={q.ref} style={{ marginBottom:16,
                                                borderLeft: matrixUnanswered ? '3px solid #ff4d4f' : '3px solid transparent',
                                                paddingLeft: matrixUnanswered ? 10 : 0, transition:'border 0.2s' }}>
                                                <div style={{ fontWeight:600, fontSize:13, marginBottom:8, color: matrixUnanswered ? '#ff4d4f' : '#333' }}>
                                                    <Tag color={sColor} style={{ color:'#fff' }}>Q{qNum}</Tag> {q.text}
                                                    {matrixUnanswered && <span style={{ fontSize:11, color:'#ff4d4f', marginLeft:6 }}>* all rows required</span>}
                                                </div>
                                                <div style={{ overflowX:'auto' }}>
                                                    <table style={{ width:'100%', borderCollapse:'collapse', minWidth:380 }}>
                                                        <thead>
                                                            <tr style={{ background:'#f5f5f5' }}>
                                                                <th style={{ padding:'6px 10px', textAlign:'left', fontSize:12 }}></th>
                                                                {q.options.map(opt => (
                                                                    <th key={opt} style={{ padding:'6px 8px', textAlign:'center', fontSize:11, color:sColor }}>{opt}</th>
                                                                ))}
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {q.sub_items.map((sub, ri) => (
                                                                <tr key={sub} style={{ background: ri%2 ? '#fafafa' : '#fff' }}>
                                                                    <td style={{ padding:'8px 10px', fontWeight:600, fontSize:13 }}>{sub}</td>
                                                                    {q.options.map(opt => (
                                                                        <td key={opt} style={{ textAlign:'center', padding:'6px' }}>
                                                                            <div onClick={() => setR(q.ref, opt, sub)} style={{
                                                                                width:26, height:26, borderRadius:'50%',
                                                                                border:`2px solid ${getR(q.ref,sub)===opt ? sColor : '#ccc'}`,
                                                                                background: getR(q.ref,sub)===opt ? sColor : 'transparent',
                                                                                margin:'0 auto', cursor:'pointer',
                                                                                display:'flex', alignItems:'center', justifyContent:'center' }}>
                                                                                {getR(q.ref,sub)===opt && <div style={{ width:12, height:12, borderRadius:'50%', background:'#fff' }}/>}
                                                                            </div>
                                                                        </td>
                                                                    ))}
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        );
                                    }

                                    if (q.type === 'yesno') {
                                        const isMedQ     = q.ref === 'IP.5' || q.ref === 'OP.6';
                                        const answeredNo  = getR(q.ref) === 'No';
                                        const isUnanswered = !getR(q.ref);
                                        return (
                                            <div key={q.ref} data-qref={q.ref} style={{ marginBottom:16, paddingBottom:12,
                                                borderBottom:'1px solid #f5f5f5',
                                                borderLeft: isUnanswered ? '3px solid #ff4d4f' : '3px solid transparent',
                                                paddingLeft: isUnanswered ? 10 : 0, transition:'border 0.2s' }}>
                                                <div style={{ fontWeight:600, fontSize:13, marginBottom:8, color: isUnanswered ? '#ff4d4f' : '#333' }}>
                                                    <Tag color={sColor} style={{ color:'#fff' }}>Q{qNum}</Tag> {q.text}
                                                    {isUnanswered && <span style={{ fontSize:11, color:'#ff4d4f', marginLeft:6 }}>* required</span>}
                                                </div>
                                                <div>
                                                    {['Yes','No'].map(opt => (
                                                        <OptionBtn key={opt} label={opt} selected={getR(q.ref)===opt} onClick={() => setR(q.ref, opt)}/>
                                                    ))}
                                                </div>
                                                {isMedQ && answeredNo && (
                                                    <div style={{ marginTop:12, background:'#fff7e6', border:'1px solid #ffd591', borderRadius:8, padding:12 }}>
                                                        <div style={{ fontSize:12, fontWeight:700, color:'#d46b08', marginBottom:10 }}>
                                                            {t('med_not_received')}
                                                        </div>
                                                        {COMMODITY_GROUPS.map(grp => (
                                                            <div key={grp.key} style={{ marginBottom:8 }}>
                                                                <div style={{ fontSize:11, fontWeight:700, color:'#666', marginBottom:4, textTransform:'uppercase', letterSpacing:1 }}>
                                                                    {grp.label}
                                                                </div>
                                                                {grp.items.map(item => {
                                                                    const checked = (notReceived[q.ref] || []).includes(item);
                                                                    return (
                                                                        <label key={item} style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4, cursor:'pointer', fontSize:13 }}>
                                                                            <input type="checkbox" checked={checked}
                                                                                onChange={e => {
                                                                                    setNotReceived(prev => {
                                                                                        const cur = prev[q.ref] || [];
                                                                                        return { ...prev, [q.ref]: e.target.checked ? [...cur, item] : cur.filter(i => i !== item) };
                                                                                    });
                                                                                }}
                                                                                style={{ width:16, height:16, accentColor:'#d46b08' }}/>
                                                                            {item}
                                                                        </label>
                                                                    );
                                                                })}
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    }

                                    // radio
                                    const isUnanswered = !getR(q.ref);
                                    return (
                                        <div key={q.ref} data-qref={q.ref} style={{ marginBottom:16, paddingBottom:12,
                                            borderBottom:'1px solid #f5f5f5',
                                            borderLeft: isUnanswered ? '3px solid #ff4d4f' : '3px solid transparent',
                                            paddingLeft: isUnanswered ? 10 : 0, transition:'border 0.2s' }}>
                                            <div style={{ fontWeight:600, fontSize:13, marginBottom:8, color: isUnanswered ? '#ff4d4f' : '#333' }}>
                                                <Tag color={sColor} style={{ color:'#fff' }}>Q{qNum}</Tag> {q.text}
                                                {isUnanswered && <span style={{ fontSize:11, color:'#ff4d4f', marginLeft:6 }}>* required</span>}
                                            </div>
                                            <div style={{ display:'flex', flexWrap:'wrap' }}>
                                                {q.options.map(opt => (
                                                    <OptionBtn key={opt} label={opt} selected={getR(q.ref)===opt}
                                                        onClick={() => setR(q.ref, opt)} color={sColor}/>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                            {section === 'ATTITUDE' && (
                                <div style={{ background:'#fff', padding:'8px 16px 12px', marginTop:-4, marginBottom:4, borderRadius:'0 0 10px 10px' }}>
                                    <label style={{ fontSize:12, fontWeight:600, color:'#888' }}>{t('comments_attitude')}</label>
                                    <textarea value={attitudeComments} onChange={e => setAttitudeComments(e.target.value)}
                                        placeholder={t('comments_optional')}
                                        style={{ width:'100%', marginTop:4, padding:'7px 10px', border:'1px solid #d9d9d9',
                                            borderRadius:6, fontSize:13, resize:'vertical', minHeight:60, boxSizing:'border-box' }}/>
                                </div>
                            )}
                            {section === 'SUPPLIES' && isIP && (
                                <div style={{ background:'#fff', padding:'8px 16px 12px', marginTop:-4, marginBottom:4, borderRadius:'0 0 10px 10px' }}>
                                    <label style={{ fontSize:12, fontWeight:600, color:'#888' }}>{t('comments_supplies')}</label>
                                    <textarea value={suppliesComments} onChange={e => setSuppliesComments(e.target.value)}
                                        placeholder={t('comments_optional')}
                                        style={{ width:'100%', marginTop:4, padding:'7px 10px', border:'1px solid #d9d9d9',
                                            borderRadius:6, fontSize:13, resize:'vertical', minHeight:60, boxSizing:'border-box' }}/>
                                </div>
                            )}
                        </div>
                    );
                })}

                {/* Final comments */}
                <div style={{ background:'#fff', borderRadius:12, padding:16, marginTop:12, marginBottom:20 }}>
                    <div style={{ fontWeight:700, fontSize:13, marginBottom:8 }}>
                        {t('final_comments_title')}
                    </div>
                    <TextArea rows={3} value={comments} onChange={e => setComments(e.target.value)}
                        placeholder={t('final_comments_placeholder')} style={{ fontSize:14 }}/>
                </div>

                {/* Consistency warnings */}
                {showWarnings && consistencyWarnings.length > 0 && (
                    <div style={{ background:'#fff7e6', border:'1px solid #ffd591',
                        borderRadius:12, padding:16, marginBottom:14 }}>
                        <div style={{ fontWeight:700, fontSize:14, color:'#d46b08', marginBottom:10 }}>
                            {t('review_answers_title')}
                        </div>
                        {consistencyWarnings.map((w, i) => (
                            <div key={i} style={{ fontSize:13, color:'#555', marginBottom:6, display:'flex', gap:8 }}>
                                <span>•</span><span>{w}</span>
                            </div>
                        ))}
                        <div style={{ display:'flex', gap:10, marginTop:14 }}>
                            <button onClick={() => { setShowWarnings(false); setConsistencyWarnings([]); }}
                                style={{ flex:1, padding:'10px', background:'#fff', border:'1px solid #ffd591',
                                    borderRadius:8, cursor:'pointer', fontWeight:600, color:'#d46b08', fontSize:13 }}>
                                {t('review_answers_btn')}
                            </button>
                            <button onClick={() => { setShowWarnings(false); handleSubmit(); }}
                                style={{ flex:1, padding:'10px', background:'#d46b08', border:'none',
                                    borderRadius:8, cursor:'pointer', fontWeight:700, color:'#fff', fontSize:13 }}>
                                {t('submit_anyway')}
                            </button>
                        </div>
                    </div>
                )}

                {/* Submit */}
                <button onClick={handleSubmit} disabled={submitting} style={{
                    width:'100%', padding:'16px', background: submitting ? '#ccc' : accent,
                    color:'#fff', border:'none', borderRadius:10, fontSize:16, fontWeight:700,
                    cursor: submitting ? 'not-allowed' : 'pointer', marginBottom:32,
                }}>
                    {submitting ? t('submitting') : completion === 100 ? t('submit_complete') : `${t('submit_incomplete')} (${completion}% done)`}
                </button>

                <div style={{ textAlign:'center', fontSize:11, color:'#aaa', paddingBottom:20 }}>
                    Ministry of Health · Eswatini · Quality of Care Program<br/>
                    Your responses are anonymous and used only for service improvement.
                </div>
            </div>
        </div>
    );
}

// ── Root component ─────────────────────────────────────────────────────────────
export default function QoCPublicSurvey() {
    const { type } = useParams();
    const history  = useHistory();

    const [lang, setLang] = useState('en');
    const t = (key) => lang === 'ss' ? (SS[key] || EN_NAMED[key] || key) : (EN_NAMED[key] || key);

    const [surveyType,  setSurveyType]  = useState(
        (type === 'inpatient' || type === 'outpatient') ? type : null
    );
    const [qrValid,     setQrValid]     = useState(false);
    const [qrChecking,  setQrChecking]  = useState(true);
    const [qrFacCode,   setQrFacCode]   = useState('');
    const [qrFacName,   setQrFacName]   = useState('');
    const [qrFacId,     setQrFacId]     = useState('');
    const [qrRegion,    setQrRegion]    = useState('');
    const [qrFacLat,    setQrFacLat]    = useState('');
    const [qrFacLng,    setQrFacLng]    = useState('');
    const [geoStatus,   setGeoStatus]   = useState('checking');
    const [geoDistance, setGeoDistance] = useState(null);
    // ── Facility services — controls which survey types appear on landing ──
    const [facServices, setFacServices] = useState([]);
    const [facSiteType, setFacSiteType] = useState('');

    useEffect(() => {
        // ── If a saved draft exists, restore directly ─────────────────────
        const draft = loadDraft();
        if (draft && draft.survey_type && draft.resp && Object.keys(draft.resp).length > 0) {
            if (draft.qrFacCode) setQrFacCode(draft.qrFacCode);
            if (draft.qrFacName) setQrFacName(draft.qrFacName);
            if (draft.qrFacId)   setQrFacId(draft.qrFacId);
            if (draft.qrRegion)  setQrRegion(draft.qrRegion);
            if (draft.qrFacLat)  setQrFacLat(draft.qrFacLat);
            if (draft.qrFacLng)  setQrFacLng(draft.qrFacLng);
            setQrValid(true);
            setGeoStatus('skipped');
            setSurveyType(draft.survey_type);
            setQrChecking(false);
            return;
        }

        // ── Normal QR verification flow ───────────────────────────────────
        const { facCode, token, timestamp, region, facLat, facLng } = getQrParams();
        const facId   = new URLSearchParams(window.location.search).get('id')   || '';
        const facName = decodeURIComponent(new URLSearchParams(window.location.search).get('name') || '');

        setQrFacCode(facCode);
        setQrFacName(facName);
        setQrFacId(facId);
        setQrRegion(decodeURIComponent(region));
        setQrFacLat(facLat);
        setQrFacLng(facLng);

        // ── Fetch facility services to filter landing page survey types ────
        if (facId || facCode) {
            axios.get(`${API_BASE}qoc/public/facilities`)
                .then(r => {
                    const facs = r.data?.data || [];
                    const match = facs.find(f =>
                        (facId   && String(f.id) === String(facId)) ||
                        (facCode && f.fac_code   === facCode)
                    );
                    if (match) {
                        if (match.services_offered) setFacServices(parseServices(match.services_offered));
                        if (match.site_type) setFacSiteType(match.site_type);
                    }
                })
                .catch(() => {});
        }

        verifyQrToken(facCode, timestamp, token).then(async (valid) => {
            setQrValid(valid);
            const isHttps = window.location.protocol === 'https:';
            if (valid && (facLat || facLng) && isHttps) {
                const geo = await checkGeolocation(facLat, facLng, 50000);
                setGeoDistance(geo.distance);
                if (geo.skipped)                         setGeoStatus('skipped');
                else if (geo.allowed)                    setGeoStatus('ok');
                else if (geo.error === 'denied')         setGeoStatus('denied');
                else if (geo.error === 'no_geolocation') setGeoStatus('unavailable');
                else                                     setGeoStatus('too_far');
            } else {
                setGeoStatus('skipped');
            }
            setQrChecking(false);
        });
    }, []);

    if (qrChecking) {
        return (
            <div style={{ minHeight:'100vh', display:'flex', alignItems:'center',
                justifyContent:'center', background:'#f0f5ff' }}>
                <div style={{ textAlign:'center', color:'#888' }}>
                    <div style={{ fontSize:32, marginBottom:12 }}>🔍</div>
                    <div style={{ marginBottom:8 }}>Verifying QR code...</div>
                    {qrFacLat && <div style={{ fontSize:12, color:'#aaa' }}>Checking your location...</div>}
                </div>
            </div>
        );
    }

    // ── Geo enforcement screens ───────────────────────────────────────────
    if (qrValid && geoStatus === 'too_far') {
        return (
            <div style={{ minHeight:'100vh', background:'#fff7e6',
                display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
                <div style={{ maxWidth:440, textAlign:'center', background:'#fff',
                    borderRadius:16, padding:'48px 32px', boxShadow:'0 8px 40px rgba(0,0,0,0.1)' }}>
                    <div style={{ fontSize:56, marginBottom:16 }}>📍</div>
                    <div style={{ fontSize:20, fontWeight:800, marginBottom:12, color:'#d46b08' }}>
                        You are not at this facility
                    </div>
                    <div style={{ color:'#666', fontSize:14, lineHeight:1.7, marginBottom:8 }}>
                        This survey is only for patients currently at <strong>{qrFacName}</strong>.
                    </div>
                    <div style={{ fontSize:13, color:'#fa8c16', fontWeight:600, marginBottom:20 }}>
                        Your distance: ~{geoDistance}m away<br/>
                        <span style={{ fontSize:11, color:'#aaa', fontWeight:400 }}>Must be within 100m of the facility</span>
                    </div>
                    <div style={{ fontSize:12, color:'#aaa' }}>
                        If you are at the facility, please ensure location services are enabled and try scanning the QR code again.
                    </div>
                </div>
            </div>
        );
    }

    if (qrValid && geoStatus === 'denied') {
        return (
            <div style={{ minHeight:'100vh', background:'#fff1f0',
                display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
                <div style={{ maxWidth:440, textAlign:'center', background:'#fff',
                    borderRadius:16, padding:'48px 32px', boxShadow:'0 8px 40px rgba(0,0,0,0.1)' }}>
                    <div style={{ fontSize:56, marginBottom:16 }}>🔒</div>
                    <div style={{ fontSize:20, fontWeight:800, marginBottom:12, color:'#cf1322' }}>
                        Location Access Required
                    </div>
                    <div style={{ color:'#666', fontSize:14, lineHeight:1.7, marginBottom:20 }}>
                        This survey requires location access to confirm you are at the health facility.
                        <br/><br/>
                        <strong>To enable:</strong> tap the lock icon in your browser address bar
                        → Site settings → Location → Allow. Then scan the QR code again.
                    </div>
                    <div style={{ fontSize:12, color:'#aaa' }}>
                        Your location is only used to verify you are at the facility. It is not stored or shared.
                    </div>
                </div>
            </div>
        );
    }

    const handleSelect = (selectedType) => {
        setSurveyType(selectedType);
        history.replace(`/qoc/survey/${selectedType}${window.location.search}`);
    };

    const handleBack = () => {
        setSurveyType(null);
        history.replace(`/qoc/survey/select${window.location.search}`);
    };

    if (!surveyType) {
        return (
            <SurveyLanding
                onSelect={handleSelect}
                qrValid={qrValid}
                qrFacCode={qrFacCode}
                qrFacName={qrFacName}
                qrRegion={qrRegion}
                geoStatus={geoStatus}
                geoDistance={geoDistance}
                facServices={facServices}
                facSiteType={facSiteType}
                lang={lang}
                setLang={setLang}
                t={t}
            />
        );
    }

    return (
        <SurveyForm
            type={surveyType}
            onBack={handleBack}
            qrFacCode={qrFacCode}
            qrFacName={qrFacName}
            qrFacId={qrFacId}
            qrRegion={qrRegion}
            qrFacLat={qrFacLat}
            qrFacLng={qrFacLng}
            lang={lang}
            setLang={setLang}
            t={t}
        />
    );
}